import { ProductModel } from './ProductModel';
import { cloneDeep, orderBy } from "lodash";
import { ProductAttributeModel } from './ProductAttributeModel';
import { ProductVariationFormModel } from './ProductVariationFormModel';
import { ProductImageModel } from './ProductImageModel';
import { ProductStoreModel } from './ProductStoreModel';
import { ProductStoreChannelModel } from './ProductStoreChannelModel';

interface VariationInfo {
    firstName: string;
    secondName: string;
    firstVariation: string;
    secondVariation: string;
}

export interface ProductBasicInfo {
    skuCode: string;
    costPrice: number;
    listPrice: number;
    sellPrice: number;
    isActive: boolean;
    eanCode: string;
    ncmCode: string;
}

export class ProductVariation {
    id?: string|number;
    height?: number;
    width?: number;
    depth?: number;
    weight?: number;
    codeSource?: string = "";
    skuCode: string = "";
    firstName: string = "";
    secondName: string = "";
    firstVariation: string = "";
    secondVariation?: string = "";
    eanCode?: string = "";
    ncmCode?: string = "";
    costPrice: number = 0;
    listPrice: number = 0;
    sellPrice: number = 0;
    isActive: boolean = false;
    stockQty: number = 0;    
    reservedStockQty?: number;    
    availableStockQty?: number;    
    images: ProductImageModel[] = [];
    stores?: ProductStoreModel[] = [];
    uniqueChannels?: ProductStoreChannelModel[] = [];
    collapsed?: boolean = true;
    stockConversionRatio: number = 0;

    constructor(pv?: ProductVariation) {
        Object.assign(this, pv);
    }
}

export class ProductBidimensionalGridModel {
    private _grid: ProductVariation[] = [];

    fromProduct(p: ProductModel):ProductBidimensionalGridModel {
        this._grid = [];
        this._mountGridFromProduct(p);
        return this;
    }

    fromProductVariationForm(p: ProductBasicInfo, v: ProductVariationFormModel):ProductBidimensionalGridModel {
        this._grid = [];
        this._mountGridFromProductVariationForm(p, v);
        this._generateSkuCode(p);
        return this;
    }

    getGrid(): ProductVariation[] {
        if (!Array.isArray(this._grid) || !this._grid.length) {
            return []
        }

        const grid = this._grid.map((g) => new ProductVariation(g))
        const gridOrdered = orderBy(grid, ['firstVariation'], ['asc']);

        return [
            ...gridOrdered.filter((item) => item.isActive),
            ...gridOrdered.filter((item) => !item.isActive),
        ]
    }

    hasGrid(): boolean {
        return !!this.getGrid().length
    }

    getFirstName(): string {
        return this._grid?.[0]?.firstName;
    }

    getSecondName(): string {
        return this._grid?.[0]?.secondName;        
    }

    private _mountGridFromProduct(p: ProductModel):ProductVariation[] {
        if (!p || !Array.isArray(p.children)) {
            return []
        }

        const children = cloneDeep(p.children);

        for (const child of children) {
            const variationInfo = this._getVariationInfo(child.getAttributeTypeGridList());

            this._grid.push({
                id: child.id,
                skuCode: child.skuCode,
                codeSource: child.codeSource,
                firstName: variationInfo.firstName,
                secondName: variationInfo.secondName,
                firstVariation: variationInfo.firstVariation,
                secondVariation: variationInfo.secondVariation,
                height: child.height,
                width: child.width,
                depth: child.depth,
                weight: child.weight,
                eanCode: child.eanCode,
                ncmCode: child.ncmCode,
                costPrice: child.costPrice,
                listPrice: child.listPrice,
                sellPrice: child.sellPrice,
                isActive: child.isActive,
                stores: child.stores,
                uniqueChannels: child.getUniqueStoreChannels(), 
                stockQty: child.totalStockQtyInStores(),
                reservedStockQty: child.totalReservedStockQtyInStores(),
                availableStockQty: child.totalAvailableStockQtyInStores(),
                images: child.getImages(),
                stockConversionRatio: child.stockConversionRatio,
            })
        }

        return this._grid
    }

    private _mountGridFromProductVariationForm(productBasicInfo: ProductBasicInfo, v: ProductVariationFormModel):ProductVariation[] {
        const variationParsed = v.getInfoParsed()
        const hasName1 = !!variationParsed.name1?.length
        const hasName2 = !!variationParsed.name2?.length
        const hasVariations1 = !!variationParsed.variations1?.length
        const hasVariations2 = !!variationParsed.variations2?.length

        if (!hasName1 && !hasName2) {
            this._grid = [];
            return this._grid;
        }

        if (!hasVariations1) {
            for (const var2 of variationParsed.variations2) {
                this._grid.push({
                    id: undefined,
                    skuCode: "",
                    firstName: variationParsed.name2,
                    secondName: "",
                    firstVariation: var2,
                    secondVariation: "",
                    height: undefined,                    
                    width: undefined,                    
                    depth: undefined,                    
                    weight: undefined,                    
                    eanCode: productBasicInfo.eanCode,
                    ncmCode: productBasicInfo.ncmCode,
                    costPrice: productBasicInfo.costPrice,
                    listPrice: productBasicInfo.listPrice,
                    sellPrice: productBasicInfo.sellPrice,
                    isActive: productBasicInfo.isActive,
                    stockQty: 0,
                    reservedStockQty: 0,
                    availableStockQty: 0,
                    stores: [],
                    uniqueChannels: [],
                    images: [],
                    stockConversionRatio: 0,
                })
            }
        }

        if (!hasVariations2) {
            for (const var1 of variationParsed.variations1) {
                this._grid.push({
                    id: undefined,
                    skuCode: "",
                    firstName: variationParsed.name1,
                    secondName: "",
                    firstVariation: var1,
                    secondVariation: "",
                    height: undefined,                    
                    width: undefined,                    
                    depth: undefined,                    
                    weight: undefined,                   
                    eanCode: productBasicInfo.eanCode,
                    ncmCode: productBasicInfo.ncmCode,
                    costPrice: productBasicInfo.costPrice,
                    listPrice: productBasicInfo.listPrice,
                    sellPrice: productBasicInfo.sellPrice,
                    isActive: productBasicInfo.isActive,
                    stockQty: 0,
                    reservedStockQty: 0,
                    availableStockQty: 0,
                    stores: [],
                    uniqueChannels: [],
                    images: [],
                    stockConversionRatio: 0,
                })
            }
        }

        for (const var1 of variationParsed.variations1) {
            for (const var2 of variationParsed.variations2) {
                this._grid.push({
                    id: undefined,
                    skuCode: "",
                    firstName: variationParsed.name1,
                    secondName: variationParsed.name2,
                    firstVariation: var1,
                    secondVariation: var2,
                    height: undefined,                    
                    width: undefined,                    
                    depth: undefined,                    
                    weight: undefined,                   
                    eanCode: productBasicInfo.eanCode,
                    ncmCode: productBasicInfo.ncmCode,
                    costPrice: productBasicInfo.costPrice,
                    listPrice: productBasicInfo.listPrice,
                    sellPrice: productBasicInfo.sellPrice,
                    isActive: productBasicInfo.isActive,
                    stockQty: 0,
                    reservedStockQty: 0,
                    availableStockQty: 0,
                    stores: [],
                    uniqueChannels: [],
                    images: [],
                    stockConversionRatio: 0,
                })
            }
        }

        return this._grid;
    }

    private _getVariationInfo(prodAttributes: ProductAttributeModel[]):VariationInfo {
        if (prodAttributes.length === 1) {
            return {
                firstName: prodAttributes[0].attribute?.name || "",
                secondName: "",
                firstVariation: prodAttributes[0].value,
                secondVariation: "",
            }
        }        

        if (prodAttributes.length === 2) {
            return {
                firstName: prodAttributes[0].attribute?.name || "",
                secondName: prodAttributes[1].attribute?.name || "",
                firstVariation: prodAttributes[0].value,
                secondVariation: prodAttributes[1].value,
            }
        }    

        return {
            firstName: "",
            secondName: "",
            firstVariation: "",
            secondVariation: "",
        }
    }

    private _generateSkuCode(p: ProductBasicInfo):void {
        if (!this._grid?.length) {
            return;
        }

        for (const row of this._grid) {
            const firstName = row.firstName.substring(0, 1)
            const secondName = (row.secondName || "").substring(0, 1)
            const firstVariation = row.firstVariation.substring(0, 4)
            const secondVariation = (row.secondVariation || "").substring(0, 4)

            row.skuCode = `${p.skuCode}${firstName}${firstVariation}${secondName}${secondVariation}`.toUpperCase()
        }
    }
}
